import { memo, useCallback, useEffect, useState } from "react";

import { getOfficesAPI } from "services/api/offices/api";
import { OfficeAddress, OfficeRow } from "services/api/offices/types";
import { getListingDefaultsAPI } from "services/api/settings/listings/api";
import { ListingContact } from "services/api/settings/listings/types";
import { initialPagination } from "ui/components/Table/consts";
import { Loader } from "ui/components/Loader";
import {
  MarketplaceContact,
  MarketplaceData,
  MarketplaceExtraData,
  MarketplaceLocation,
} from "../../types";
import { mapOfficeAddressToLocation } from "../../mapping/officeLocation";
import WarrantySection from "../MarketplaceStep/sections/WarrantySection";
import LocationSection from "../MarketplaceStep/sections/LocationSection";
import ContactSection from "../MarketplaceStep/sections/ContactSection";
import { AdditionalInfoStepProps } from "./types";

// Jamstvo, lokacija i kontakt su izdvojeni iz koraka „Podaci oglasa" — opisuju ponudu
// oko vozila, a ne samo vozilo, i taj je korak inače postao predugačak za jedan ekran.
//
// Poslovnice i kontakti iz postavki dohvaćaju se ovdje, a ne u sekcijama: sekcije iz njih
// računaju koja je kartica odabrana, pa bi im odabir poskočio kad podaci stignu naknadno.
const AdditionalInfoStep: React.FC<AdditionalInfoStepProps> = (props) => {
  const { formValues, setFormValues, errors } = props;
  const md: MarketplaceData = formValues.marketplaceData;

  const [isLoading, setIsLoading] = useState(true);
  const [offices, setOffices] = useState<OfficeRow[]>([]);
  const [settingsContacts, setSettingsContacts] = useState<ListingContact[]>([]);

  useEffect(() => {
    const loadOptions = async () => {
      const [officeResult, defaults] = await Promise.all([
        getOfficesAPI({ ...initialPagination, pageSize: 200 }).catch(() => null),
        getListingDefaultsAPI().catch(() => null),
      ]);

      setOffices(officeResult?.data ?? []);
      setSettingsContacts(defaults?.contacts ?? []);
      setIsLoading(false);
    };

    loadOptions();
  }, []);

  const patchMarketplace = useCallback(
    (patch: Partial<MarketplaceData>) =>
      setFormValues((prev) => ({
        ...prev,
        marketplaceData: { ...prev.marketplaceData, ...patch },
      })),
    [setFormValues]
  );

  const handleExtra = useCallback(
    (patch: Partial<MarketplaceExtraData>) =>
      patchMarketplace({ extra: { ...md.extra, ...patch } }),
    [md.extra, patchMarketplace]
  );

  const handleLocation = useCallback(
    (patch: Partial<MarketplaceLocation>) =>
      patchMarketplace({ location: { ...md.location, ...patch } }),
    [md.location, patchMarketplace]
  );

  const handleContact = useCallback(
    (patch: Partial<MarketplaceContact>) =>
      patchMarketplace({ contact: { ...md.contact, ...patch } }),
    [md.contact, patchMarketplace]
  );

  // Odabir poslovnice dira dvije grane odjednom (kontakt + lokacija), pa ide jednim
  // patchem — dva odvojena setFormValues poziva bi se ovdje mogla pregaziti.
  // Lokacija uvijek prati poslovnicu: bez nje se polja prazne, da na oglasu ne ostane
  // adresa poslovnice koja s njim više nema veze.
  const handleOfficeChange = useCallback(
    (officeId: string, address: OfficeAddress | null) =>
      patchMarketplace({
        contact: {
          ...md.contact,
          locationOfficeIds: officeId ? [officeId] : [],
        },
        location: { ...md.location, ...mapOfficeAddressToLocation(address) },
      }),
    [md.contact, md.location, patchMarketplace]
  );

  return (
    <div className="w-full max-w-5xl mx-auto px-6 py-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold text-slate-800">
          Dodatne informacije
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Jamstvo, lokacija vozila i kontakt koji se prikazuju na oglasu.
        </p>
      </div>

      {isLoading ? (
        <div className="relative flex items-center justify-center min-h-[400px]">
          <Loader isLoading={true}> </Loader>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <WarrantySection values={md.extra} onChange={handleExtra} />
          <LocationSection
            values={md.location}
            onChange={handleLocation}
            officeId={md.contact.locationOfficeIds[0] ?? ""}
            onOfficeChange={handleOfficeChange}
            offices={offices}
            error={errors["marketplaceData.location.manualAddress"]}
          />
          <ContactSection
            values={md.contact}
            onChange={handleContact}
            settingsContacts={settingsContacts}
            error={errors["marketplaceData.contact.contactId"]}
          />
        </div>
      )}
    </div>
  );
};

export default memo(AdditionalInfoStep);
